import { useRef, useState } from "react";
import { OnChangeValue, Value } from ".";
import useDebounce from "../../../lib/useDebounce";
import { useQuery } from "react-query";
import axiosClient from "../../../lib/axios";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { TContaCorrente } from "../../../types/api/financeiro/conta-corrente";
import { IDetalharTituloPagar } from "../../../types/api/financeiro/detalhar-titulo-pagar";

export default function DialogSelectItems({
  open,
  handleClose,
  title,
  defValue,
  onSubmitValue,
  titulo,
}: DialogSelectItemsProps) {
  const [keyword, setKeyword] = useState<string>("");
  const [selected, setSelected] = useState<Value>(defValue);
  const [limit, setLimit] = useState<number>(50);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  //queries e mutations
  const debouncedKeyword = useDebounce(keyword);
  const { data, isFetching, isLoading } = useQuery({
    queryKey: [
      "/financeiro/contas-receber/pesquisar-contas-corrente",
      debouncedKeyword,
      limit,
    ],
    queryFn: () =>
      axiosClient.get("/financeiro/contas-receber/pesquisar-contas-corrente", {
        params: {
          page: 1,
          limit: limit,
          codigo_publico: titulo?.publico?.codigo_publico,
          tipo_publico: titulo?.publico?.tipo_publico,
          pesquisar: debouncedKeyword,
        },
      }),
    retry: false,
    keepPreviousData: true,
    cacheTime: 0,
  });
  const dadosContaCorrente: TContaCorrente[] = data?.data.data || [];
  //

  //funções manipuladoras
  const handleSubmit = () => {
    if (selected) {
      onSubmitValue(selected);
      handleClose();
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const bottom =
      event.currentTarget.scrollHeight - event.currentTarget.scrollTop ===
      event.currentTarget.clientHeight;
    if (bottom && !isFetching) {
      setLimit((prevLimit) => prevLimit + 20);
    }
  };
  //

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        scroll="body"
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Selecionar {title}</DialogTitle>

        <DialogContent>
          <Box py={2}>
            <TextField
              fullWidth
              value={keyword}
              label="Pesquisar"
              onChange={({ target }) => setKeyword(target.value)}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }
              }}
            />

            <Box mt={1}>{isFetching && data && <LinearProgress />}</Box>
            {!isLoading && data && (
              <TableContainer
                ref={tableContainerRef}
                onScroll={handleScroll}
                sx={{ height: 400, overflow: "auto", mb: 1 }}
              >
                <Table size="small" aria-label="a dense table" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>
                        <b>Banco</b>
                      </TableCell>
                      <TableCell>
                        <b>Agência</b>
                      </TableCell>
                      <TableCell>
                        <b>Conta</b>
                      </TableCell>
                      <TableCell>
                        <b>Tipo conta bancária</b>
                      </TableCell>
                      <TableCell>
                        <b>Tipo Conta</b>
                      </TableCell>
                      <TableCell>
                        <b>Conta leite</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dadosContaCorrente.map((row) => {
                      const identificador = row.id.toString();
                      const radioMarcado = selected?.id === row.id;
                      return (
                        <TableRow
                          key={row.id}
                          sx={{
                            cursor: "pointer",
                            backgroundColor: radioMarcado ? "#edf7ed" : "unset",
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                          onClick={() => {
                            setSelected(row);
                          }}
                        >
                          <TableCell>
                            <Radio
                              name="radio-buttons"
                              checked={radioMarcado}
                              slotProps={{ input: { "aria-label": identificador } }}
                            />
                          </TableCell>
                          <TableCell>{row.banco?.nome || "-"}</TableCell>
                          <TableCell>
                            {row.agencia && row.agencia_digito
                              ? `${row.agencia}-${row.agencia_digito}`
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {row.conta && row.conta_digito
                              ? `${row.conta}-${row.conta_digito}`
                              : "-"}
                          </TableCell>

                          <TableCell>
                            {(() => {
                              switch (row.tipo_conta_bancaria) {
                                case "C":
                                  return "Conta Corrente";
                                case "P":
                                  return "Conta Poupança";
                                default:
                                  return "-";
                              }
                            })()}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              switch (row.tipo_cnpj_conta) {
                                case "F":
                                  return "PF";
                                case "J":
                                  return "PJ";
                                default:
                                  return "-";
                              }
                            })()}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              switch (row.conta_leite) {
                                case "S":
                                  return "Sim";
                                case "N":
                                  return "Não";
                                default:
                                  return "-";
                              }
                            })()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button autoFocus variant="contained" onClick={handleSubmit}>
            Selecionar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

interface DialogSelectItemsProps {
  open: boolean;
  handleClose: () => void;
  title: string;
  defValue: Value;
  onSubmitValue: OnChangeValue;
  titulo?: Partial<IDetalharTituloPagar>;
}
